import { useEffect } from 'react';
import { Modal, Stack, TextInput, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Video, Monitor, Globe, Lock } from 'react-feather';
import { coachesApi } from '../../../services/api/coaches';
import type { Coach, UpdateCoachRequest } from '../../../types';

interface EditSessionSettingsModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditSessionSettingsModal({
  opened,
  onClose,
  coach,
  onSaved,
}: EditSessionSettingsModalProps) {
  const form = useForm<Pick<UpdateCoachRequest, 'zoom_link' | 'zoom_passcode' | 'teams_link' | 'google_meet_link'>>({
    initialValues: {
      zoom_link: '',
      zoom_passcode: '',
      teams_link: '',
      google_meet_link: '',
    },
    validate: {
      zoom_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
      teams_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
      google_meet_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        zoom_link: coach.zoom_link || '',
        zoom_passcode: coach.zoom_passcode || '',
        teams_link: coach.teams_link || '',
        google_meet_link: coach.google_meet_link || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when modal opens with new coach data
  }, [opened, coach]);

  const handleSubmit = async (values: Pick<UpdateCoachRequest, 'zoom_link' | 'zoom_passcode' | 'teams_link' | 'google_meet_link'>) => {
    try {
      const payload: UpdateCoachRequest = {
        zoom_link: values.zoom_link?.trim() || null,
        zoom_passcode: values.zoom_passcode?.trim() || null,
        teams_link: values.teams_link?.trim() || null,
        google_meet_link: values.google_meet_link?.trim() || null,
      };
      const updatedCoach = await coachesApi.update(coach.id, payload);
      notifications.show({
        title: 'Success',
        message: 'Session settings updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update session settings',
        color: 'red',
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Session Settings" size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Zoom Personal Meeting Link"
            placeholder="https://..."
            leftSection={<Video size={16} />}
            {...form.getInputProps('zoom_link')}
          />
          <TextInput
            label="Zoom Passcode"
            placeholder="Passcode of the Zoom link"
            leftSection={<Lock size={16} />}
            maxLength={100}
            {...form.getInputProps('zoom_passcode')}
          />
          <TextInput
            label="Microsoft Teams Meeting Link"
            placeholder="https://..."
            leftSection={<Monitor size={16} />}
            {...form.getInputProps('teams_link')}
          />
          <TextInput
            label="Google Meet Link"
            placeholder="https://..."
            leftSection={<Globe size={16} />}
            {...form.getInputProps('google_meet_link')}
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
